import { useMemo } from 'react';
import Image from 'next/legacy/image';
import styled from 'styled-components';
import { Checkbox, Form, Input } from 'antd';

import useI18n from '../../common/hooks/useI18n';

type Props = {
  onSubscribeButtonClick: (email: string) => void;
};

type FormValues = {
  email: string;
  privacyPolicyAccepted: boolean;
};

const Banner = styled.div`
  position: relative;
  height: 40vh;
  min-height: 280px;
  background-image: url(/images/tangram_left.png);
  background-size: 100% 100%;
  background-position: center center;
  background-repeat: no-repeat;

  @media ${(props) => props.theme.breakpoints.lg} {
    min-height: 320px;
  }
  @media ${(props) => props.theme.breakpoints.md} {
    min-height: 280px;
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    height: unset;
    min-height: 400px;
    background-image: unset;
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;

  padding: 14px ${(props) => props.theme.grid.getGridColumns(2, 1)};
  @media ${(props) => props.theme.breakpoints.xxl} {
    padding: 14px ${(props) => props.theme.grid.getGridColumns(1, 1)};
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: -1;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;

  .image {
    object-fit: contain;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    .image {
      object-fit: cover;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  width: 100%;
  @media ${(props) => props.theme.breakpoints.xs} {
    border-radius: ${(props) => props.theme.borderRadius};
    background-color: #ffffff;
    opacity: 0.9;
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);
    padding: 32px;
  }
`;

const SectionTitle = styled.h3`
  max-width: ${(props) => props.theme.grid.getGridColumns(14, 1)};
  @media ${(props) => props.theme.breakpoints.sm} {
    max-width: ${(props) => props.theme.grid.getGridColumns(10, 1)};
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    width: 100%;
  }

  text-align: right;

  color: ${(props) => props.theme.colors.secondary};

  ${(props) => props.theme.font.h2}
`;

const StyledForm = styled(Form)`
  width: ${(props) => props.theme.grid.getGridColumns(7, 1)};
  @media ${(props) => props.theme.breakpoints.lg} {
    width: ${(props) => props.theme.grid.getGridColumns(9, 1)};
  }
  @media ${(props) => props.theme.breakpoints.md} {
    width: ${(props) => props.theme.grid.getGridColumns(10, 1)};
  }
  @media ${(props) => props.theme.breakpoints.sm} {
    width: ${(props) => props.theme.grid.getGridColumns(10, 1)};
  }
  @media ${(props) => props.theme.breakpoints.xs} {
    width: 100%;
  }
  & > .ant-form-item:last-of-type {
    margin-bottom: 0px;
  }
`;

const Divider = styled.hr`
  width: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-top: 16px;
  margin-bottom: 32px;
  margin-left: var(--gutter);
  margin-right: 14px;
  border: 1px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.borderRadius};
`;

const NewsletterSection: React.FC<Props> = ({ onSubscribeButtonClick }) => {
  const i18n = useI18n();

  const [form] = Form.useForm();
  const validateMessages = {
    required: i18n.t('contactForm.validateRequired'),
    types: {
      email: i18n.t('contactForm.validateEmail'),
    },
  };

  const privacyPolicyAcceptedText = useMemo(
    () => i18n.t('contactForm.privacyPolicyAccepted'),
    [i18n]
  );

  return (
    <Banner>
      <ImageAbsoluteContainer>
        <ImageContainer>
          <Image
            alt="banner_newsletter.png"
            className="image"
            layout="fill"
            objectPosition="left center"
            src="/images/banner_newsletter.png"
            unoptimized
          />
        </ImageContainer>
      </ImageAbsoluteContainer>
      <Container>
        <SectionTitle>{i18n.t('homePage.newsletterTitle')}</SectionTitle>
        <Divider />
        <StyledForm
          validateMessages={validateMessages}
          form={form}
          onFinish={(values: FormValues) =>
            onSubscribeButtonClick(values.email)
          }
        >
          <Form.Item name="email" rules={[{ required: true, type: 'email' }]}>
            <Input.Search
              enterButton={i18n.t('contactForm.send')}
              placeholder={i18n.t('contactForm.email')}
              onSearch={() => form.submit()}
            />
          </Form.Item>
          <Form.Item
            name="privacyPolicyAccepted"
            valuePropName="checked"
            initialValue={false}
            rules={[
              {
                required: true,
                transform: (value) => value || undefined, // https://github.com/ant-design/ant-design/issues/8261#issuecomment-439833733
                type: 'boolean',
                message: i18n.t('contactForm.validatePrivacyPolicy'),
              },
            ]}
          >
            <Checkbox>
              <span
                key={
                  privacyPolicyAcceptedText.substring(0, 10) +
                  new Date().getTime()
                }
                dangerouslySetInnerHTML={{
                  __html: privacyPolicyAcceptedText,
                }}
              />
            </Checkbox>
          </Form.Item>
        </StyledForm>
      </Container>
    </Banner>
  );
};

export default NewsletterSection;
